import { useState } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { Button } from '@/components/ui/button'
import {
  Shield,
  Users,
  BarChart3,
  Wrench,
  Link,
  LogOut,
  Settings,
  Home,
  Phone,
  Share2,
  Image,
  BellRing,
  BookOpen,
  Menu,
  X,
  Building2,
  Calendar,
  Lightbulb,
  LayoutGrid,
  Scale,
  Megaphone,
  Award,
} from 'lucide-react'

import { VillageChiefManager } from '@/components/admin/village-chief-manager'
import { ResidentsManager } from '@/components/admin/residents-manager'
import { StatisticsManager } from '@/components/admin/statistics-manager'
import { ServicesManager } from '@/components/admin/services-manager'
import { GovernmentLinksManager } from '@/components/admin/government-links-manager'
import { ContactInfoManager } from '@/components/admin/contact-info-manager'
import { SocialLinksManager } from '@/components/admin/social-links-manager'
import { HeroManager } from '@/components/admin/hero-manager'
import { AlertsManager } from '@/components/admin/alerts-manager'
import { MustKnowLawsManager } from '@/components/admin/must-know-laws-manager'
import { OfficialsManager } from '@/components/admin/officials-manager'
import { GalleryManager } from '@/components/admin/gallery-manager'
import { MaintenanceModeManager } from '@/components/admin/maintenance-mode-manager'
import { ResultLinksManager } from '@/components/admin/result-links-manager'
import { EventsManager } from '@/components/admin/events-manager'
import { LogoManager } from '@/components/admin/logo-manager'
import { AwarenessManager } from '@/components/admin/awareness-manager'
import { LawAwarenessManager } from '@/components/admin/law-awareness-manager'

interface AdminItem {
  value: string
  label: string
  icon: any
  hint: string
}

interface AdminGroup {
  id: string
  label: string
  items: AdminItem[]
}

/**
 * One flat list per group — no nested collapsibles, no per-item colour. Every
 * section is reachable in a single click.
 */
const GROUPS: AdminGroup[] = [
  {
    id: 'homepage',
    label: 'Home Page',
    items: [
      { value: 'hero', label: 'Hero Banner', icon: Image, hint: 'Title, tagline and background photo' },
      { value: 'chief', label: 'Village Chief', icon: Shield, hint: 'Sarpanch details and message' },
      { value: 'statistics', label: 'Statistics', icon: BarChart3, hint: 'Population, households, schools' },
      { value: 'services', label: 'Services', icon: Wrench, hint: 'Local services and phone numbers' },
      { value: 'links', label: 'Government Links', icon: Link, hint: 'Useful portals for villagers' },
    ],
  },
  {
    id: 'community',
    label: 'Community',
    items: [
      { value: 'residents', label: 'Residents', icon: Users, hint: 'Community member directory' },
      { value: 'officials', label: 'Officials', icon: Building2, hint: 'Officials and police stations' },
      { value: 'events', label: 'Events', icon: Calendar, hint: 'Village events and dates' },
      { value: 'gallery', label: 'Gallery', icon: Image, hint: 'Village photographs' },
    ],
  },
  {
    id: 'awareness',
    label: 'Awareness & Info',
    items: [
      { value: 'awareness', label: 'Awareness Images', icon: Lightbulb, hint: 'Awareness campaign posters' },
      { value: 'law-awareness', label: 'Law Awareness', icon: Scale, hint: 'Legal awareness content' },
      { value: 'laws', label: 'Must Know Laws', icon: BookOpen, hint: 'Rights and legal guides' },
      { value: 'result-links', label: 'Result Links', icon: Award, hint: 'Exam result shortcuts' },
    ],
  },
  {
    id: 'settings',
    label: 'Site Settings',
    items: [
      { value: 'contact', label: 'Contact Info', icon: Phone, hint: 'Panchayat contact details' },
      { value: 'social', label: 'Social Links', icon: Share2, hint: 'Social media profiles' },
      { value: 'alerts', label: 'Alerts', icon: BellRing, hint: 'Site-wide announcements' },
      { value: 'logo', label: 'Site Logo', icon: Megaphone, hint: 'Header logo image' },
      { value: 'maintenance', label: 'Maintenance Mode', icon: Settings, hint: 'Take the site offline' },
    ],
  },
]

const ALL_ITEMS = GROUPS.flatMap((g) => g.items)

export default function AdminDashboard() {
  const { user, signOut, loading } = useAuth()
  const [activeTab, setActiveTab] = useState('overview')
  const [sidebarOpen, setSidebarOpen] = useState(false)

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-slate-500 text-sm">Loading dashboard…</p>
        </div>
      </div>
    )
  }

  if (!user) {
    window.location.href = '/admin-login'
    return null
  }

  const handleSignOut = async () => {
    await signOut()
    window.location.href = '/admin-login'
  }

  const open = (value: string) => {
    setActiveTab(value)
    setSidebarOpen(false)
  }

  const active = ALL_ITEMS.find((i) => i.value === activeTab)

  const renderContent = () => {
    switch (activeTab) {
      case 'overview': return <Overview onOpen={open} />
      case 'hero': return <HeroManager />
      case 'chief': return <VillageChiefManager />
      case 'residents': return <ResidentsManager />
      case 'statistics': return <StatisticsManager />
      case 'services': return <ServicesManager />
      case 'links': return <GovernmentLinksManager />
      case 'contact': return <ContactInfoManager />
      case 'social': return <SocialLinksManager />
      case 'alerts': return <AlertsManager />
      case 'laws': return <MustKnowLawsManager />
      case 'logo': return <LogoManager />
      case 'officials': return <OfficialsManager />
      case 'gallery': return <GalleryManager />
      case 'awareness': return <AwarenessManager />
      case 'law-awareness': return <LawAwarenessManager />
      case 'result-links': return <ResultLinksManager />
      case 'events': return <EventsManager />
      case 'maintenance': return <MaintenanceModeManager />
      default: return <Overview onOpen={open} />
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200 flex flex-col transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center gap-3 h-16 px-5 border-b border-slate-200">
          <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <Shield className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 leading-tight">Sandalpur</p>
            <p className="text-xs text-slate-500 leading-tight">Admin</p>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <NavButton
            icon={LayoutGrid}
            label="Overview"
            active={activeTab === 'overview'}
            onClick={() => open('overview')}
          />
          {GROUPS.map((group) => (
            <div key={group.id} className="mt-5">
              <p className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                {group.label}
              </p>
              {group.items.map((item) => (
                <NavButton
                  key={item.value}
                  icon={item.icon}
                  label={item.label}
                  active={activeTab === item.value}
                  onClick={() => open(item.value)}
                />
              ))}
            </div>
          ))}
        </nav>

        <div className="border-t border-slate-200 p-3">
          <div className="flex items-center gap-2.5 px-2 py-1.5">
            <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
              <span className="text-xs font-semibold text-slate-600">
                {user.email?.charAt(0).toUpperCase()}
              </span>
            </div>
            <p className="text-xs text-slate-600 truncate flex-1" title={user.email ?? ''}>
              {user.email}
            </p>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6">
            <div className="flex items-center gap-3 min-w-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden -ml-2"
              >
                {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
              <div className="min-w-0">
                <h1 className="text-base font-semibold text-slate-900 truncate">
                  {active?.label ?? 'Overview'}
                </h1>
                {active?.hint && (
                  <p className="text-xs text-slate-500 truncate hidden sm:block">{active.hint}</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => (window.location.href = '/')}
                className="hidden sm:flex items-center gap-2"
              >
                <Home className="h-4 w-4" />
                <span>View Site</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={handleSignOut} title="Sign out">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6 max-w-6xl">{renderContent()}</main>
      </div>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  )
}

function NavButton({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  icon: any
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
        active
          ? 'bg-primary/10 text-primary font-medium'
          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
      }`}
    >
      <Icon className={`h-4 w-4 shrink-0 ${active ? 'text-primary' : 'text-slate-400'}`} />
      <span className="truncate">{label}</span>
    </button>
  )
}

/** Landing view: every section as a plain card, so nothing is buried in a menu. */
function Overview({ onOpen }: { onOpen: (value: string) => void }) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">What would you like to update?</h2>
        <p className="text-sm text-slate-500 mt-1">
          Changes appear on the website and in the Sandalpur app.
        </p>
      </div>

      {GROUPS.map((group) => (
        <section key={group.id}>
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-3">
            {group.label}
          </h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {group.items.map((item) => (
              <button
                key={item.value}
                onClick={() => onOpen(item.value)}
                className="text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-primary/40 hover:shadow-sm transition-all"
              >
                <div className="flex items-start gap-3">
                  <span className="h-9 w-9 rounded-lg bg-slate-50 flex items-center justify-center shrink-0">
                    <item.icon className="h-4 w-4 text-slate-500" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900">{item.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5 leading-snug">{item.hint}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}
